// src/support/slices/salesforceCustomerSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchItems } from '../queries';
import { Customer } from '../../types/customer';
import { RootState } from '../store';
import { UserView } from '../../types/user';
import { defaultColumns, defaultViews } from '../config/tables';


interface SalesforceCustomerState {
  views: {
    [viewId: string]: {
      customers: Customer[];
      totalCount: number;
      page: number;
      size: number;
      view: UserView;
      search: string;
    };
  };
  loading: boolean;
  initialLoadComplete: boolean;
  error: string | null;
}

const initialState: SalesforceCustomerState = {
  views: {},
  initialLoadComplete: false,
  loading: false,
  error: null,
};

export const fetchSalesforceCustomers = createAsyncThunk<
  { customers: Customer[]; totalCount: number; view: UserView },
  { view: UserView },
  { state: RootState }
>(
  'salesforceCustomers/fetchSalesforceCustomers',
  async ({ view }, { getState }) => {
    const state = getState();
    const { page = 0, size = 30 } = state.salesforceCustomers.views[view.id] || {};
    const body = {
      filters: view.filters ?? [],
      order_by: `${view.orderBy?.column ?? 'Name'} ${view.orderBy?.order?.toUpperCase() ?? 'ASC'}`,
      columns: (view.columns ?? defaultColumns.customer).map((column) => column.id),
      search_string: state.salesforceCustomers.views[view.id]?.search ?? ''
    };

    const fetchedCustomers = await fetchItems('sf/customer', { page: page + 1, size }, { pagination: true, body });
    const customers = (fetchedCustomers.items ?? []).map((customer) => ({
        ...customer,
        id: customer.Id ?? customer.id,
      } as Customer));
    return { customers, totalCount: fetchedCustomers.total ?? 0, view };
  }
);

const salesforceCustomersSlice = createSlice({
  name: 'salesforceCustomers',
  initialState,
  reducers: {
    removeCustomerView(state, action: PayloadAction<{ viewId: UserView['id'] }>) {
      if (state.views[action.payload.viewId]) {
        delete state.views[action.payload.viewId];
      }
    },
    setCustomerView(state, action: PayloadAction<{ viewId: UserView['id'], options?: Record<string, any> }>) {
      state.views[action.payload.viewId] = {
        customers: [],
        totalCount: 0,
        page: 0,
        size: 30,
        view: action.payload.options?.view ?? defaultViews.customer,
        search: '',
      };
    },
    setPage(state, action: PayloadAction<{ viewId: UserView['id'], page: number }>) {
      const { viewId, page } = action.payload;
      if (state.views[viewId]) {
        state.views[viewId].page = page;
      }
    },
    setSize(state, action: PayloadAction<{ viewId: UserView['id'], size: number }>) {
      const { viewId, size } = action.payload;
      if (state.views[viewId]) {
        state.views[viewId].size = size;
        state.views[viewId].page = 0;
      }
    },
    setSearch(state, action: PayloadAction<{ viewId: UserView['id'], search: string }>) {
      const { viewId, search } = action.payload;
      if (state.views[viewId]) {
        state.views[viewId].search = search;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesforceCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesforceCustomers.fulfilled, (
        state,
        action: PayloadAction<{ customers: Customer[]; totalCount: number; view: UserView }>
      ) => {
        const { customers, totalCount, view } = action.payload;
        state.views[view.id] = {
          ...state.views[view.id],
          customers,
          totalCount,
          view
        };
        state.initialLoadComplete = true;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchSalesforceCustomers.rejected, (state, action) => {
        state.initialLoadComplete = true;
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  removeCustomerView,
  setCustomerView,
  setPage,
  setSize,
  setSearch
} = salesforceCustomersSlice.actions;

export default salesforceCustomersSlice.reducer;